// eslint-disable-next-line import/no-extraneous-dependencies
import { OpenAI } from "openai";
import { z } from "zod";

import { getNormalEnum } from "./pseudoEnums.js";

export const OpenAIEmbeddingProvider = z.literal("OPENAI");
export const BasetenEmbeddingProvider = z.literal("BASETEN");
// Provider enum
export const LanceEmbeddingProviderLiteral = z.union([
  OpenAIEmbeddingProvider,
  BasetenEmbeddingProvider,
]);
export const LanceEmbeddingProvider = getNormalEnum(
  LanceEmbeddingProviderLiteral,
);
export type LanceEmbeddingProvider = z.infer<
  typeof LanceEmbeddingProviderLiteral
>;

const OpenAIEmbeddingModelLiteral = z.enum([
  // OpenAI (see https://platform.openai.com/docs/guides/embeddings/embedding-models#embedding-models)
  "text-embedding-3-small",
  "text-embedding-3-large",
  "text-embedding-ada-002",
]);
export const OpenAIEmbeddingModel = getNormalEnum(
  OpenAIEmbeddingModelLiteral,
) satisfies Record<
  OpenAI.Embeddings.EmbeddingModel,
  OpenAI.Embeddings.EmbeddingModel
>;
export type OpenAIEmbeddingModel = z.infer<typeof OpenAIEmbeddingModelLiteral>;

export const BasetenEmbeddingModelLiteral = z.enum([
  "snowflake-arctic-embed-l-v2.0",
]);
export const BasetenEmbeddingModel = getNormalEnum(
  BasetenEmbeddingModelLiteral,
);
export type BasetenEmbeddingModel = z.infer<
  typeof BasetenEmbeddingModelLiteral
>;

export const LanceEmbeddingModelLiteral = OpenAIEmbeddingModelLiteral.or(
  BasetenEmbeddingModelLiteral,
);
export type LanceEmbeddingModel = z.infer<typeof LanceEmbeddingModelLiteral>;
export const LanceEmbeddingModel = {
  ...OpenAIEmbeddingModel,
  ...BasetenEmbeddingModel,
};

// Provider to models mapping
export const LanceEmbeddingModelMap = {
  [LanceEmbeddingProvider.OPENAI]: OpenAIEmbeddingModel,
  [LanceEmbeddingProvider.BASETEN]: BasetenEmbeddingModel,
};
export type LanceEmbeddingModelMap = typeof LanceEmbeddingModelMap;

export const OpenAIEmbeddingModelProviderKey = z.object({
  provider: OpenAIEmbeddingProvider,
  model: OpenAIEmbeddingModelLiteral,

  embeddingVersion: z.string(),
  schemaVersion: z.string(),
});
export type OpenAIEmbeddingModelProviderKey = z.infer<
  typeof OpenAIEmbeddingModelProviderKey
>;

export const BasetenEmbeddingModelProviderKey = z.object({
  provider: BasetenEmbeddingProvider,
  model: BasetenEmbeddingModelLiteral,

  embeddingVersion: z.string(),
  schemaVersion: z.string(),
});
export type BasetenEmbeddingModelProviderKey = z.infer<
  typeof BasetenEmbeddingModelProviderKey
>;

export const DEFAULT_MPK: EmbeddingModelProviderKeyType = {
  model: LanceEmbeddingModelMap.OPENAI["text-embedding-ada-002"],
  provider: LanceEmbeddingProvider.OPENAI,

  embeddingVersion: "0",
  schemaVersion: "0",
};

export const EmbeddingModelProviderKey = OpenAIEmbeddingModelProviderKey.or(
  BasetenEmbeddingModelProviderKey,
);
export type EmbeddingModelProviderKeyType = z.infer<
  typeof EmbeddingModelProviderKey
>;

export const LanceTableOfHexesSchemaVersionLiteral = z.enum(["0", "1", "2"]);
export type LanceTableOfHexesSchemaVersion = z.infer<
  typeof LanceTableOfHexesSchemaVersionLiteral
>;
export const LanceTableOfHexesSchemaVersionEnum = getNormalEnum(
  LanceTableOfHexesSchemaVersionLiteral,
);

export const LanceTableOfHexesEmbeddingVersionLiteral = z.enum(["0"]);
export type LanceTableOfHexesEmbeddingVersion = z.infer<
  typeof LanceTableOfHexesEmbeddingVersionLiteral
>;
export const LanceTableOfHexesEmbeddingVersionEnum = getNormalEnum(
  LanceTableOfHexesEmbeddingVersionLiteral,
);

export const LanceTableOfDataSourceColumnsSchemaVersionLiteral = z.enum([
  "0",
  "1",
]);
export type LanceTableOfDataSourceColumnsSchemaVersion = z.infer<
  typeof LanceTableOfDataSourceColumnsSchemaVersionLiteral
>;
export const LanceTableOfDataSourceColumnsSchemaVersionEnum = getNormalEnum(
  LanceTableOfDataSourceColumnsSchemaVersionLiteral,
);

export const LanceTableOfDataSourceColumnsEmbeddingVersionLiteral = z.enum([
  "0",
]);
export type LanceTableOfDataSourceColumnsEmbeddingVersion = z.infer<
  typeof LanceTableOfDataSourceColumnsEmbeddingVersionLiteral
>;
export const LanceTableOfDataSourceColumnsEmbeddingVersionEnum = getNormalEnum(
  LanceTableOfDataSourceColumnsEmbeddingVersionLiteral,
);
